//Libraries
import { FC, useState, useEffect } from "react";
import { Dialog, Grid } from "@mui/material";
import { Button } from "@cb/apricot-react";

//Interfaces & Typing
import { IDI } from "src/types/org/di/diInterface";
import { Draft } from "src/types/common";

//Processing
import { ChangeManager } from "src/generic/utils/form";
import { IFieldErrors, ErrorManager } from "src/types/validation";

//Components
import { FieldRow, Panel, Title, RadioLabel, IconButton, FieldNote, FieldLabel, Highlight, Divider } from "src/generic/component";
import { Input, Radio, Dropdown, Checkbox } from "src/generic/component/form";
import { DiSearch } from "src/org/component/orgSearch";
import { AddressFields, formatAddress } from "src/generic/component/address";
import { ContactFields } from "src/generic/component/contact";

//Providers
import { useReferences } from 'src/services';

interface IDiFieldProps {
  readOnly?: boolean;
  item: Draft<IDI>;
  validating?: boolean;
  errors: IFieldErrors;
  original?: Draft<IDI>;
  hints?: any;
  showDiff?: boolean;
  showProtected?: boolean;
  onUpdate?: (address: Draft<IDI>) => void;
}

export const DiFields: FC<IDiFieldProps> = ({ hints, original, showProtected, item: initial, readOnly, errors, onUpdate }) => {
  const { countries } = useReferences() || {}
  const [isMergeSearchOpen, setMergeSearchOpen] = useState(false);
  const [searchParams, setSearchParams] = useState<any>({});

  const merge = hints?.merge || {};
  const isMerge = hints?.isMerge || false;
  const zip = merge.address?.zip5;
  const postalCd = merge.address?.intlPostalCd;
  const countryIsoCd = merge.address?.countryIsoCd;

  useEffect(() => {
    setSearchParams({
      name: merge.name,
      countryIsoCd: countryIsoCd,
      zip: zip,
      postalCd: postalCd,
      diCode: merge.diCode,
    });
  }, [hints]);

  /**
   * Form Variables and Monitoring Funcationality
   *
   * This is the place where most additions and modifications will occur for the form
   * in addition to the rendering block.
   */
  const {
    diCode,
    diMergedCode,
    apEligibilityInd,
    // clepEligibilityInd,
    satEligibilityInd,
    apContact,
    // clepContact,
    satContact,
    apAddress,
    // clepAddress
  } = initial || {};

  const showMerge = !apEligibilityInd && !satEligibilityInd && isMerge;

  const { getError, getFieldError, hasError } = ErrorManager(errors);
  const { format, formatOption, formatBoolean, isChanged, getChangeColor } = ChangeManager<IDI>(original || {}, initial);

  const changed = (updates: Draft<IDI>) => {
    if (onUpdate) {
      onUpdate({ ...initial, ...updates });
    }
  };

  return (
    <>
      <Panel>
        <Title>DI Code Information</Title>
        {diCode && (
          <FieldRow>
            <strong>Updating DI: {diCode}</strong>
          </FieldRow>
        )}
        <Grid container spacing={5}>
          <Grid item xs={6}>
            {showMerge && (
              <>
                <Grid container style={{ marginBottom: "10px" }}>
                  <Grid item>
                    <FieldRow>
                      <RadioLabel>
                        <div style={{ display: "inline-block", marginRight: "5px" }}>Merged DI Code:</div>
                        <Highlight style={{ display: "inline-block", marginRight: "5px" }}>{diMergedCode || "No DI Selected"}</Highlight>
                        <div style={{ lineHeight: 1, marginBottom: "8px" }}>
                          <FieldNote>(If you are merging, use the search to choose this DI.)</FieldNote>
                        </div>
                      </RadioLabel>
                      {original && isChanged("diMergedCode") && (
                        <div style={{ marginBottom: "5px" }}>
                          <i className={getChangeColor("diMergedCode")}>Current: {format("diMergedCode")}</i>
                        </div>
                      )}
                      {!readOnly &&
                        (diMergedCode ? (
                          <>
                            {diMergedCode && (
                              <IconButton
                                onClick={() => {
                                  changed({ diMergedCode: undefined });
                                }}
                              >
                                Change
                              </IconButton>
                            )}
                          </>
                        ) : (
                          <>
                            <Button
                              onClick={() => {
                                setMergeSearchOpen(true);
                              }}
                            >
                              Search DIs
                            </Button>
                            <Dialog fullWidth={true} maxWidth={"md"} open={isMergeSearchOpen}>
                              <div style={{ padding: "20px" }}>
                                <FieldRow>
                                  <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                      <Input
                                        onChange={(e) => {
                                          setSearchParams({ ...searchParams, name: e.target.value });
                                        }}
                                        placeholder="Name"
                                        value={searchParams.name}
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Input
                                        onChange={(e) => {
                                          setSearchParams({ ...searchParams, text: e.target.value });
                                        }}
                                        placeholder="Text Search"
                                        value={searchParams.text}
                                      />
                                    </Grid>
                                    <Grid item xs={4}>
                                      <Input
                                        onChange={(e) => {
                                          setSearchParams({ ...searchParams, diCode: e.target.value });
                                        }}
                                        placeholder="DI Code"
                                        value={searchParams.diCode}
                                      />
                                    </Grid>
                                    <Grid item xs={4}>
                                      {countries && <Dropdown
                                        values={countries.map((option) => ({ key: option.value, label: option.label }))}
                                        onChange={(e) => {
                                          setSearchParams({ countryIsoCd: e.target.value, zip: undefined, postalCd: undefined });
                                        }}
                                        value={searchParams.countryIsoCd}
                                      />}
                                    </Grid>
                                    {searchParams.countryIsoCd && (
                                      <Grid item xs={4}>
                                        {searchParams.countryIsoCd === "US" ? (
                                          <Input
                                            onChange={(e) => {
                                              setSearchParams({ ...searchParams, zip: e.target.value, postalCd: undefined });
                                            }}
                                            placeholder="Zip"
                                            value={searchParams.zip}
                                          />
                                        ) : (
                                          <Input
                                            onChange={(e) => {
                                              setSearchParams({ ...searchParams, zip: undefined, postalCd: e.target.value });
                                            }}
                                            placeholder="Postal Code"
                                            value={searchParams.postalCd}
                                          />
                                        )}
                                      </Grid>
                                    )}
                                  </Grid>
                                </FieldRow>
                                <Divider />
                                <DiSearch
                                  params={{
                                    dicode: searchParams.diCode,
                                    name: searchParams.name,
                                    zip: searchParams.zip,
                                    intl_postal_cd: searchParams.postalCd,
                                    iso: searchParams.countryIsoCd,
                                    text: searchParams.text,
                                  }}
                                  onSelect={(match) => {
                                    changed({ diMergedCode: match.diCode });
                                    setMergeSearchOpen(false);
                                  }}
                                />
                                <Button
                                  onClick={() => {
                                    setMergeSearchOpen(false);
                                  }}
                                >
                                  Close
                                </Button>
                              </div>
                            </Dialog>
                          </>
                        ))}
                    </FieldRow>
                  </Grid>
                  <Grid item container sm justifyContent={"flex-start"}>
                    <Grid item xs container direction="column" spacing={2}></Grid>
                    <Grid item>
                      {hints?.merge && (
                        <FieldNote>
                          User Submitted:
                          <div style={{ lineHeight: "18px" }}>
                            {merge.diCode && <div>DI Code - {merge.diCode}</div>}
                            {merge.name && (
                              <>
                                <div>{merge.name}</div>
                                {formatAddress(merge.address)}
                              </>
                            )}
                          </div>
                        </FieldNote>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
                <Divider />
              </>
            )}
          </Grid>
          <Grid item xs={6}></Grid>
        </Grid>
        <>
          <div>
            <FieldRow>
              <FieldLabel variant="body1" style={{ marginBottom: "4px" }}>
                Program scores accepted:
              </FieldLabel>

              <div style={{ display: "flex", columnGap: "15px" }}>
                <Checkbox
                  disabled={readOnly}
                  label="SAT"
                  style={{ display: "inline-block" }}
                  checked={satEligibilityInd}
                  onChange={() => {
                    changed({ satEligibilityInd: !satEligibilityInd });
                  }}
                />
                <Checkbox
                  disabled={readOnly}
                  label="AP"
                  style={{ display: "inline-block" }}
                  checked={apEligibilityInd}
                  onChange={() => {
                    changed({ apEligibilityInd: !apEligibilityInd });
                  }}
                />
                {/* <Checkbox disabled={readOnly} label='CLEP' style={{ display: 'inline-block' }} checked={clepEligibilityInd} onChange={() => {
              changed({clepEligibilityInd: !clepEligibilityInd})
            }} /> */}
              </div>
            </FieldRow>
          </div>

          <br />
          <Grid container spacing={5}>
            {satEligibilityInd && (
              <Grid item xs={6}>
                <FieldRow>
                  <strong>SAT Contact Information</strong>
                </FieldRow>
                <ContactFields
                  readOnly={readOnly}
                  errors={getFieldError("satContact")}
                  contact={satContact}
                  showDiff={original ? true : false}
                  original={original?.satContact || {}}
                  onUpdate={(contact) => {
                    changed({ satContact: contact });
                  }}
                />
              </Grid>
            )}
            {apEligibilityInd && (
              <Grid item xs={6}>
                <FieldRow>
                  <strong>AP Contact Information</strong>
                </FieldRow>
                <ContactFields
                  readOnly={readOnly}
                  errors={getFieldError("apContact")}
                  contact={apContact}
                  showDiff={original ? true : false}
                  original={original?.apContact || {}}
                  onUpdate={(contact) => {
                    changed({ apContact: contact });
                  }}
                />
                <FieldRow>
                  <strong>AP Address</strong>
                </FieldRow>
                <AddressFields
                  readOnly={readOnly}
                  errors={getFieldError("apAddress")}
                  address={apAddress || {}}
                  showDiff={original ? true : false}
                  original={original?.apAddress || {}}
                  onUpdate={(address) => {
                    changed({ apAddress: address });
                  }}
                />
              </Grid>
            )}
            {/* { clepEligibilityInd && 
        <Grid item xs={6}>
        <FieldRow><strong>CLEP Contact Information</strong></FieldRow>
          <ContactFields readOnly={readOnly} errors={getFieldError('clepContact')} contact={clepContact}
            showDiff={original ? true : false}
            original={original?.clepContact || {}}
            onUpdate={(contact) => {
              changed({ clepContact: contact });
            }} />
          <FieldRow><strong>CLEP Address</strong></FieldRow>
          <AddressFields readOnly={readOnly} errors={getFieldError('clepAddress')} address={clepAddress}
            showDiff={original ? true : false}
            original={original?.clepAddress || {}}
            onUpdate={(address) => {
              changed({ clepAddress: address });
            }} />
        </Grid>
        }  */}
          </Grid>
        </>
      </Panel>
    </>
  );
};
